import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function LoginPage() {
  const { login, error } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    if (ok) {
      const params = new URLSearchParams(loc.search);
      const redirect = params.get("redirect");
      nav(redirect || loc.state?.from || "/dashboard", { replace: true });
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-zinc-950 text-zinc-50">
      <div className="relative hidden lg:flex items-center justify-center overflow-hidden border-r border-zinc-800">
        <img
          alt=""
          src="https://static.prod-images.emergentagent.com/jobs/6b2fe78c-239f-4612-a5e1-80a7026e27df/images/5a00876beeabdcdf491a9c48fa75e07ad2dd9782c856eeb36279c8d15338b000.png"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />
        <div className="relative z-10 p-12 max-w-lg">
          <div className="label-eyebrow mb-4">Digital Ad Journal Generator</div>
          <h1 className="font-display text-5xl xl:text-6xl font-bold tracking-tight leading-[1.05]">
            Build a slideshow your sponsors will brag about.
          </h1>
          <p className="mt-6 text-zinc-300 leading-relaxed max-w-md">
            Bulk-import ads, frame each tier in its own color, then export as a fullscreen slideshow, MP4, PDF book — or raw 1920×1080 PNGs for Premiere.
          </p>
        </div>
        <div className="grain" />
      </div>

      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={onSubmit}
          data-testid="login-form"
          className="w-full max-w-sm space-y-6"
        >
          <div>
            <div className="label-eyebrow mb-3">Sign in</div>
            <h2 className="font-display text-3xl font-bold">Welcome back</h2>
            <p className="text-sm text-zinc-400 mt-2">Use your admin or registered account.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="label-eyebrow">Email</Label>
            <Input
              data-testid="login-email"
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-blue-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="label-eyebrow">Password</Label>
            <Input
              data-testid="login-password"
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-blue-500"
            />
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/60 rounded-md p-3">
              {error}
            </div>
          )}

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>

          <p className="text-sm text-zinc-400 text-center">
            No account?{" "}
            <Link to="/register" data-testid="goto-register" className="text-blue-400 hover:text-blue-300 underline-offset-4 hover:underline">
              Create one
            </Link>
          </p>
          <div className="pt-2 border-t border-zinc-900 text-center">
            <Link
              to="/explore"
              data-testid="goto-explore"
              className="text-xs text-zinc-500 hover:text-zinc-200 transition-colors inline-flex items-center gap-1"
            >
              Browse public caption libraries →
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
